import AuthenticationType from '@alltrails/amplitude/enums/AuthenticationType';
import logLogInSuccess from '@alltrails/amplitude/events/logLogInSuccess';
import logSignUpSuccess from '@alltrails/amplitude/events/logSignUpSuccess';
import { setUserId } from '@alltrails/amplitude/utils/track';
import { trackCompleteRegistrationForFacebook } from '@alltrails/core/utils/marketingTracking/facebook';
import { trackCompleteRegistrationForOpenAi } from '@alltrails/core/utils/marketingTracking/openAi';
import { trackCompleteRegistrationForReddit } from '@alltrails/core/utils/marketingTracking/reddit';
import { trackCompleteRegistrationForSnapchat } from '@alltrails/core/utils/marketingTracking/snapchat';
import { trackTinuitiRegistration } from '@alltrails/core/utils/marketingTracking/tinuitiUtils';
import { trackWeatherRegistration } from '@alltrails/core/utils/marketingTracking/weatherDotComPixel';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type LoginResponse from '../types/LoginResponse';

type Data = Pick<LoginResponse, 'is_sign_up'> & {
  user: Pick<LoginResponse['user'], 'emailSha256'> & {
    id: any;
  };
  currencyCode?: CurrencyCode | null;
};

// Please see OAuthHandlers for handling apple, facebook, google, etc. login/signups

// TODO: Eventually consolidate some of this event tracking logic on backend and
// move this tracking there instead to be more accurate, improve client performance,
// and avoid GDPR cookie gotchas.
// Note: Sending user_ids client-side is still important for merging web users with mobile users.
const authSuccessCallback = (data: Data, authType: AuthenticationType, isCollabListInvite?: boolean) => {
  const { is_sign_up, user, currencyCode } = data;
  if (is_sign_up) {
    // TODO: require as argument + pass-in user.id to logSignupSuccess instead of setting outside
    setUserId(user?.id);

    window.Sprig?.('identifyAndTrack', {
      eventName: 'Sign_Up_Success',
      userId: user?.id
    });

    if (isCollabListInvite) {
      logSignUpSuccess({ authentication_type: authType, deferred_deep_link_type: 'collaborative list' });
    } else {
      logSignUpSuccess({ authentication_type: authType });
    }

    // GA
    if (typeof gtag !== 'undefined') {
      // Set GA user id
      // https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id
      gtag('config', window.ga4_measure_id, {
        user_id: user.id
      });
      // GA UA
      let pagePart;
      if (authType === 'email') {
        pagePart = '/';
      } else if (authType === 'google one tap') {
        pagePart = '/google/';
      } else {
        pagePart = `/${authType}/`;
      }
      gtag('event', 'page_view', { page_path: `/register${pagePart}success` });
      // GA4
      // https://developers.google.com/analytics/devguides/collection/ga4/reference/events#sign_up
      gtag('event', 'sign_up', { method: authType });
    }
    // FB Pixel
    trackCompleteRegistrationForFacebook(user.id, user.emailSha256, authType);

    // Reddit Pixel
    trackCompleteRegistrationForReddit(user.id, currencyCode);

    // Snapchat Pixel
    trackCompleteRegistrationForSnapchat(user.id, user.emailSha256);

    // OpenAI Pixel
    trackCompleteRegistrationForOpenAi(user.id);

    // Microsoft UET Pixel
    window.uetq = window.uetq || [];
    window.uetq.push('event', 'sign_up', {});

    // Weather.com Pixel
    trackWeatherRegistration();

    // Tinuiti Pixel
    trackTinuitiRegistration(user.id);
  } else {
    // TODO: require as argument + pass-in user.id to logLoginSuccess instead of setting outside
    setUserId(user?.id);
    if (isCollabListInvite) {
      logLogInSuccess({
        authentication_type: authType,
        deferred_deep_link_type: 'collaborative list'
      });
    } else {
      logLogInSuccess({ authentication_type: authType });
    }
  }
};

export { authSuccessCallback };
